import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ChapterHeading, Reveal } from "./Reveal";

const SLIDES = [
  { id: "family-room-twin", src: "/images/gallery/family-room-twin.jpg", caption: "Family room · two doubles" },
  { id: "family-room-ac", src: "/images/gallery/family-room-ac.jpg", caption: "Double room · AC" },
  { id: "deluxe-room-carpet", src: "/images/gallery/deluxe-room-carpet.jpg", caption: "Spacious double" },
  { id: "private-bathroom", src: "/images/gallery/private-bathroom.jpg", caption: "Private bathroom" },
  { id: "double-room-maroon", src: "/images/gallery/double-room-maroon.jpg", caption: "Double room" },
  { id: "family-room-wide", src: "/images/gallery/family-room-wide.jpg", caption: "Family room · two doubles" },
  { id: "double-room-grey", src: "/images/gallery/double-room-grey.jpg", caption: "Double room" },
  { id: "twin-room", src: "/images/gallery/twin-room.jpg", caption: "Twin room" },
  { id: "double-room-brown", src: "/images/gallery/double-room-brown.jpg", caption: "Family room" },
  { id: "double-room-window", src: "/images/gallery/double-room-window.jpg", caption: "Double room" },
  { id: "family-room-runners", src: "/images/gallery/family-room-runners.jpg", caption: "Family room" },
];

export const Gallery = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section id="gallery" data-testid="gallery-section" className="border-t border-white/5 bg-[#0b0b0d]">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
        <ChapterHeading number="02" label="Inside the House" testId="gallery-chapter" />

        <div className="flex flex-wrap items-end justify-between gap-8">
          <Reveal>
            <h2 className="max-w-xl font-serif text-4xl leading-[1.05] tracking-tight text-[#F5F5F0] sm:text-5xl lg:text-6xl">
              Clean rooms, <em className="font-light italic text-[#C5A059]">honest</em> comfort.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="flex items-center gap-4">
            <span data-testid="gallery-counter" className="font-serif text-lg text-[#A1A1AA]">
              {String(selected + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
            </span>
            <button
              data-testid="gallery-prev-button"
              onClick={scrollPrev}
              aria-label="Previous photos"
              className="rounded-full border border-[#C5A059]/40 p-3 text-[#C5A059] transition-[transform,background-color] duration-500 hover:-translate-y-0.5 hover:bg-[#C5A059]/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              data-testid="gallery-next-button"
              onClick={scrollNext}
              aria-label="Next photos"
              className="rounded-full border border-[#C5A059]/40 p-3 text-[#C5A059] transition-[transform,background-color] duration-500 hover:-translate-y-0.5 hover:bg-[#C5A059]/10"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mt-14">
          <div data-testid="gallery-carousel" className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {SLIDES.map((s) => (
                <figure
                  key={s.id}
                  data-testid={`gallery-slide-${s.id}`}
                  className="group relative min-w-0 flex-[0_0_88%] pr-5 sm:flex-[0_0_60%] lg:flex-[0_0_42%]"
                >
                  <div className="overflow-hidden rounded-2xl">
                    <img
                      src={s.src}
                      alt={`${s.caption} at Pipewalas House & Homestays`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <figcaption className="pointer-events-none absolute inset-x-5 bottom-0 rounded-b-2xl bg-gradient-to-t from-[#080809]/85 to-transparent p-5 pt-14">
                    <span className="font-serif text-lg italic text-[#F5F5F0]/90">{s.caption}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <p className="mt-6 text-xs text-[#A1A1AA]/70">
            Drag or use the arrows — every photo is from the house itself.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
